import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPortal,
  AlertDialogTitle,
  Button,
} from '@commander/ui';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import {
  RiArrowLeftLine,
  RiDeleteBinLine,
  RiPencilLine,
  RiPushpinFill,
  RiPushpinLine,
} from '@remixicon/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ThreadSummary } from '../types.js';

function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;

  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;

  return new Date(dateStr).toLocaleDateString();
}

const TIME_GROUPS = ['Pinned', 'Today', 'Yesterday', 'This Week', 'This Month', 'Older'] as const;

function getTimeGroup(thread: ThreadSummary): (typeof TIME_GROUPS)[number] {
  if (thread.pinned_at) return 'Pinned';

  const now = new Date();
  const date = new Date(thread.updated_at);

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);

  if (date >= startOfToday) return 'Today';
  if (date >= startOfYesterday) return 'Yesterday';

  // Start of week (Sunday)
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
  if (date >= startOfWeek) return 'This Week';

  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  if (date >= startOfMonth) return 'This Month';

  return 'Older';
}

function InlineEditTitle({
  title,
  onSave,
  onCancel,
}: {
  title: string;
  onSave: (newTitle: string) => void;
  onCancel: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(title);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleSave = useCallback(() => {
    const trimmed = value.trim();
    if (trimmed && trimmed !== title) {
      onSave(trimmed);
    } else {
      onCancel();
    }
  }, [value, title, onSave, onCancel]);

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={handleSave}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
          e.preventDefault();
          handleSave();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
      }}
      onClick={(e) => e.stopPropagation()}
      onKeyUp={(e) => e.stopPropagation()}
      className="w-full text-sm font-medium bg-transparent border border-border rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-ring"
      maxLength={100}
    />
  );
}

interface ThreadListProps {
  threads: ThreadSummary[];
  activeThreadId: string;
  onSelectThread: (id: string) => void;
  onDeleteThread: (id: string) => void;
  onRenameThread: (id: string, title: string) => void;
  onTogglePinThread: (id: string) => void;
  onClearAllThreads: () => void;
  onBack: () => void;
  portalContainer?: HTMLElement | null;
  hasMoreThreads?: boolean;
  onLoadMore?: () => void;
}

export function ThreadList({
  threads,
  activeThreadId,
  onSelectThread,
  onDeleteThread,
  onRenameThread,
  onTogglePinThread,
  onClearAllThreads,
  onBack,
  portalContainer,
  hasMoreThreads,
  onLoadMore,
}: ThreadListProps) {
  const [deleteTarget, setDeleteTarget] = useState<ThreadSummary | null>(null);
  const [clearAllOpen, setClearAllOpen] = useState(false);
  const [editingThreadId, setEditingThreadId] = useState<string | null>(null);

  const groupedThreads = useMemo(() => {
    const groups = new Map<string, ThreadSummary[]>();
    for (const thread of threads) {
      const group = getTimeGroup(thread);
      const existing = groups.get(group);
      if (existing) existing.push(thread);
      else groups.set(group, [thread]);
    }
    return TIME_GROUPS.filter((g) => groups.has(g)).map((g) => ({
      label: g,
      threads: groups.get(g) ?? [],
    }));
  }, [threads]);

  return (
    <div className="flex flex-1 flex-col min-h-0">
      <div className="flex items-center gap-2 border-b px-4 py-3">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onBack}>
          <RiArrowLeftLine className="h-4 w-4" />
        </Button>
        <span className="text-sm font-medium flex-1">Chat History</span>
        {threads.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-muted-foreground hover:text-destructive"
            onClick={() => setClearAllOpen(true)}
          >
            Clear all
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {threads.length === 0 ? (
          <div className="flex items-center justify-center p-6">
            <p className="text-sm text-muted-foreground">No conversations yet.</p>
          </div>
        ) : (
          <div className="flex flex-col">
            {groupedThreads.map((group) => (
              <div key={group.label}>
                <div className="sticky top-0 z-10 bg-background px-4 pt-4 pb-1.5">
                  <span className="text-xs font-medium text-muted-foreground">{group.label}</span>
                </div>
                {group.threads.map((thread) => (
                  <button
                    type="button"
                    key={thread.id}
                    className={`group flex items-center gap-3 border-b px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors w-full text-left ${
                      thread.id === activeThreadId ? 'bg-muted/30' : ''
                    }`}
                    onClick={() => {
                      if (editingThreadId !== thread.id) onSelectThread(thread.id);
                    }}
                    onKeyDown={(e) => {
                      if (editingThreadId === thread.id) {
                        e.stopPropagation();
                        e.preventDefault();
                      }
                    }}
                  >
                    {thread.pinned_at && (
                      <RiPushpinFill className="h-3 w-3 text-muted-foreground shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      {editingThreadId === thread.id ? (
                        <InlineEditTitle
                          title={thread.title || 'Untitled conversation'}
                          onSave={(newTitle) => {
                            onRenameThread(thread.id, newTitle);
                            setEditingThreadId(null);
                          }}
                          onCancel={() => setEditingThreadId(null)}
                        />
                      ) : (
                        <div className="flex items-center gap-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {thread.title || 'Untitled conversation'}
                          </p>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingThreadId(thread.id);
                            }}
                            title="Rename conversation"
                          >
                            <RiPencilLine className="h-3.5 w-3.5 text-muted-foreground" />
                          </Button>
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {formatRelativeTime(thread.updated_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={(e) => {
                          e.stopPropagation();
                          onTogglePinThread(thread.id);
                        }}
                        title={thread.pinned_at ? 'Unpin conversation' : 'Pin conversation'}
                      >
                        {thread.pinned_at ? (
                          <RiPushpinFill className="h-3.5 w-3.5 text-muted-foreground" />
                        ) : (
                          <RiPushpinLine className="h-3.5 w-3.5 text-muted-foreground" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(thread);
                        }}
                        title="Delete conversation"
                      >
                        <RiDeleteBinLine className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                    </div>
                  </button>
                ))}
              </div>
            ))}
            {hasMoreThreads && onLoadMore && (
              <button
                type="button"
                className="w-full py-3 text-xs text-muted-foreground hover:text-foreground transition-colors"
                onClick={onLoadMore}
              >
                Load older conversations
              </button>
            )}
          </div>
        )}
      </div>

      {/* Delete single thread dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogPortal container={portalContainer}>
          <AlertDialogPrimitive.Content className="absolute left-[50%] top-[50%] z-50 grid w-full max-w-sm translate-x-[-50%] translate-y-[-50%] gap-4 border bg-muted p-6 shadow-lg sm:rounded-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete conversation</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete &ldquo;
                {deleteTarget?.title || 'Untitled conversation'}
                &rdquo;? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => {
                  if (deleteTarget) {
                    onDeleteThread(deleteTarget.id);
                    setDeleteTarget(null);
                  }
                }}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogPrimitive.Content>
        </AlertDialogPortal>
      </AlertDialog>

      {/* Clear all threads dialog */}
      <AlertDialog open={clearAllOpen} onOpenChange={setClearAllOpen}>
        <AlertDialogPortal container={portalContainer}>
          <AlertDialogPrimitive.Content className="absolute left-[50%] top-[50%] z-50 grid w-full max-w-sm translate-x-[-50%] translate-y-[-50%] gap-4 border bg-muted p-6 shadow-lg sm:rounded-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
            <AlertDialogHeader>
              <AlertDialogTitle>Clear all conversations</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete all {threads.length} conversation
                {threads.length !== 1 ? 's' : ''}? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => {
                  onClearAllThreads();
                  setClearAllOpen(false);
                }}
              >
                Clear all
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogPrimitive.Content>
        </AlertDialogPortal>
      </AlertDialog>
    </div>
  );
}
